import { apiService } from './apiService';
import {
  EnglishAnalyticsMe,
  EnglishCompositionResult,
  EnglishCompositionSubmission,
  EnglishExamReadingAnswerSubmission,
  EnglishExamReadingResult,
  EnglishExamReadingSession,
  EnglishLiveMode,
  EnglishLiveSessionCompletion,
  EnglishLiveSessionGrant,
  EnglishReadingAttempt,
  EnglishReadingSession,
  EnglishState,
} from '../types/english';

export const englishService = {
  async getState(subjectId: string): Promise<EnglishState> {
    const response = await apiService.request<EnglishState>({
      url: `/english/state?subject_id=${encodeURIComponent(subjectId)}`,
      method: 'GET',
    });
    return response.data;
  },

  async getMyAnalytics(subjectId: string): Promise<EnglishAnalyticsMe> {
    const response = await apiService.request<EnglishAnalyticsMe>({
      url: `/english/analytics/me?subject_id=${encodeURIComponent(subjectId)}`,
      method: 'GET',
    });
    return response.data;
  },

  async dismissNudge(subjectId: string): Promise<void> {
    const formData = new FormData();
    formData.append('subject_id', subjectId);
    await apiService.request({
      url: '/english/nudge/dismiss',
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  async createCompositionSubmission(
    subjectId: string,
    payload: {
      promptTopic?: string;
      submissionText?: string;
      files?: File[];
    },
  ): Promise<EnglishCompositionSubmission> {
    const formData = new FormData();
    formData.append('subject_id', subjectId);
    if (payload.promptTopic?.trim()) {
      formData.append('prompt_topic', payload.promptTopic.trim());
    }
    if (payload.submissionText?.trim()) {
      formData.append('submission_text', payload.submissionText.trim());
    }
    (payload.files || []).slice(0, 3).forEach((file) => {
      formData.append('files', file);
    });
    const response = await apiService.request<EnglishCompositionSubmission>({
      url: '/english/composition/submissions',
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async markCompositionSubmission(
    submissionId: string,
    confirmedText: string,
  ): Promise<EnglishCompositionResult> {
    const response = await apiService.request<EnglishCompositionResult>({
      url: `/english/composition/submissions/${encodeURIComponent(submissionId)}/mark`,
      method: 'POST',
      data: {
        confirmed_text: confirmedText,
      },
    });
    return response.data;
  },

  async createExamReadingSession(subjectId: string): Promise<EnglishExamReadingSession> {
    const formData = new FormData();
    formData.append('subject_id', subjectId);
    const response = await apiService.request<EnglishExamReadingSession>({
      url: '/english/exam-reading/sessions',
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async submitExamReadingSession(
    sessionId: string,
    readingTimeSeconds: number,
    answers: EnglishExamReadingAnswerSubmission[],
  ): Promise<EnglishExamReadingResult> {
    const response = await apiService.request<EnglishExamReadingResult>({
      url: `/english/exam-reading/sessions/${encodeURIComponent(sessionId)}/submit`,
      method: 'POST',
      data: {
        reading_time_seconds: Math.max(Math.round(readingTimeSeconds), 0),
        answers,
      },
    });
    return response.data;
  },

  async issueLiveSessionToken(subjectId: string, mode: EnglishLiveMode): Promise<EnglishLiveSessionGrant> {
    const formData = new FormData();
    formData.append('subject_id', subjectId);
    const url = mode === 'oral_reading' ? '/english/live/oral/token' : '/english/live/speaking/token';
    const response = await apiService.request<EnglishLiveSessionGrant>({
      url,
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async completeLiveSession(
    sessionId: string,
    transcript: string,
    actualDurationSeconds: number,
    evidence: Record<string, unknown> = {},
    feedback: Record<string, unknown> = {},
  ): Promise<EnglishLiveSessionCompletion> {
    const response = await apiService.request<EnglishLiveSessionCompletion>({
      url: `/english/live/sessions/${encodeURIComponent(sessionId)}/complete`,
      method: 'POST',
      data: {
        transcript,
        actual_duration_seconds: Math.max(Math.round(actualDurationSeconds), 0),
        evidence,
        feedback,
      },
    });
    return response.data;
  },

  // Legacy read-aloud API kept for backward compatibility.
  async createReadingSession(subjectId: string): Promise<EnglishReadingSession> {
    const formData = new FormData();
    formData.append('subject_id', subjectId);
    const response = await apiService.request<EnglishReadingSession>({
      url: '/english/reading/sessions',
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async submitReadingAttempt(
    sessionId: string,
    audio: Blob,
    durationSeconds: number,
  ): Promise<EnglishReadingAttempt> {
    const formData = new FormData();
    formData.append('file', audio, 'read-aloud.webm');
    formData.append('duration', String(Math.max(Math.round(durationSeconds), 0)));
    const response = await apiService.request<EnglishReadingAttempt>({
      url: `/english/reading/sessions/${encodeURIComponent(sessionId)}/attempts`,
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};
